import { useFeatureFlagsStore } from '@renderer/stores/featureFlagsStore'
import { useSessionStateStore } from '@renderer/stores/sessionState'
import { addPreviewConsumer, hasPreviewConsumers } from '@renderer/stores/cameraRestStore'
import { unifiedCameraUtils } from './uniCamUtils'

/**
 * Camera Preview Refcount (flag `camera_preview_refcount`, Phase 4 of
 * docs/camera-rest-plan.md): preview-driven camera lifecycle.
 *
 * Every mounted <Preview> holds a reference on its cameraKey. Transitions:
 *   0 → 1 : wake the camera if parked (mounting ANY preview — capture page,
 *           settings modal, filter page — is the "I need the camera" signal).
 *   1 → 0 : start a linger timer; when it fires with the count still 0 AND no
 *           active photo session, PARK the camera (mirror down, connection kept).
 *
 * The linger absorbs page transitions (Capture → Filter → Retake remount their
 * Previews well inside the window), and the active-session check pins the camera
 * awake across preview-less pages (Select/Checkout/Payment) so a mid-session
 * park/wake cycle never adds latency to a paid session.
 *
 * Parking itself is `unifiedCameraUtils.parkCamera`, which no-ops unless the
 * `camera_rest` flag is ALSO on (and refuses to park mid-capture/recovery), so
 * this flag without camera_rest degrades to pure no-op bookkeeping. This module
 * deliberately does NOT connect cameras (that stays with View/tryToConnect) —
 * it only parks/wakes an existing connection.
 */
export const CAMERA_PREVIEW_REFCOUNT_FLAG = 'camera_preview_refcount'

export function isPreviewRefcountEnabled(): boolean {
  return useFeatureFlagsStore.getState().getFlag(CAMERA_PREVIEW_REFCOUNT_FLAG, false)
}

type CameraKey = 'primary' | 'secondary'

/** How long the last preview must stay unmounted before the camera parks. */
const PARK_LINGER_MS = 45_000

const lingerTimers: Record<CameraKey, ReturnType<typeof setTimeout> | null> = {
  primary: null,
  secondary: null
}

function clearLinger(cameraKey: CameraKey): void {
  const timer = lingerTimers[cameraKey]
  if (timer) {
    clearTimeout(timer)
    lingerTimers[cameraKey] = null
  }
}

function scheduleLingerPark(cameraKey: CameraKey): void {
  clearLinger(cameraKey)
  lingerTimers[cameraKey] = setTimeout(() => {
    lingerTimers[cameraKey] = null
    if (hasPreviewConsumers(cameraKey)) return
    // An active session pins the camera awake even with no preview mounted
    // (Select/Checkout/Payment/Result must not pay a wake on the way to Retake).
    if (useSessionStateStore.getState().id) {
      console.log(`[PREVIEW-REFCOUNT] Linger elapsed for ${cameraKey} but session active — not parking`)
      return
    }
    console.log(`[PREVIEW-REFCOUNT] No preview consumers for ${cameraKey}; parking`)
    void unifiedCameraUtils.parkCamera(cameraKey)
  }, PARK_LINGER_MS)
}

/**
 * Register a mounted preview consumer. Returns the matching release function
 * (idempotent — safe to call from a React effect cleanup that might double-fire).
 *
 * The COUNT itself is always kept (in cameraRestStore) so every park authority
 * can see "a liveview is on screen" regardless of flags; only the park-on-release
 * linger behaviour is gated by `camera_preview_refcount`.
 */
export function acquireCameraPreview(cameraKey: CameraKey): () => void {
  const releaseConsumer = addPreviewConsumer(cameraKey)
  clearLinger(cameraKey)
  // Wake no-ops when not parked, so this is free in the common case. A mounted
  // preview must never sit on a parked camera — that is the frozen-frame bug.
  void unifiedCameraUtils.wakeCamera(cameraKey)

  let released = false
  return (): void => {
    if (released) return
    released = true
    releaseConsumer()
    if (!hasPreviewConsumers(cameraKey) && isPreviewRefcountEnabled()) {
      scheduleLingerPark(cameraKey)
    }
  }
}
